import { create } from 'zustand'

// ---------------------------------------------------------------------------
// Shared UI state for the left "Ask AI" rail.
//
// The agent panel now lives in the left rail (where the project list used to
// be). Both the rail container (Sidebar) and the agent dock (AskAIDock) need to
// agree on the same view state — expanded / minimised / maximized and the
// resizable width — so it's lifted into this tiny store rather than threaded
// through props across ModeRouter → *AgentDock → DockShell.
//
//   panelOpen=false            → minimised: a thin vertical rail (status + stop)
//   panelOpen=true, maximized  → full-viewport panel (main content hidden)
//   panelOpen=true             → normal: resizable expanded panel
// ---------------------------------------------------------------------------

const WIDTH_KEY = 'twasta_dock_width'
const OPEN_KEY = 'twasta_dock_open'
const SHOW_DONE_KEY = 'twasta_dock_show_done_plans'

export const MIN_DOCK_WIDTH = 300
export const MAX_DOCK_WIDTH = 680
export const DEFAULT_DOCK_WIDTH = 380
export const MINIMISED_RAIL_WIDTH = 64

function initWidth(): number {
  const v = Number(localStorage.getItem(WIDTH_KEY))
  if (Number.isFinite(v) && v >= MIN_DOCK_WIDTH && v <= MAX_DOCK_WIDTH) return v
  return DEFAULT_DOCK_WIDTH
}

function initOpen(): boolean {
  const saved = localStorage.getItem(OPEN_KEY)
  if (saved !== null) return saved === 'true'
  // Desktop: agent visible by default (it's the primary left-rail feature).
  // Mobile: collapsed so the page gets the full width.
  return window.innerWidth >= 768
}

interface DockUiState {
  panelOpen: boolean
  maximized: boolean
  width: number
  projectModalOpen: boolean
  // Completed plans are hidden from the Plans list by default (they pile up
  // and clutter the dock). The 3-dot header menu toggles this to reveal them.
  showCompletedPlans: boolean
  setPanelOpen: (v: boolean) => void
  togglePanel: () => void
  setMaximized: (v: boolean) => void
  setWidth: (v: number) => void
  setProjectModalOpen: (v: boolean) => void
  setShowCompletedPlans: (v: boolean) => void
}

export const useDockUiStore = create<DockUiState>((set) => ({
  panelOpen: initOpen(),
  maximized: false,
  width: initWidth(),
  projectModalOpen: false,
  showCompletedPlans: localStorage.getItem(SHOW_DONE_KEY) === 'true',

  setPanelOpen: (v) => {
    localStorage.setItem(OPEN_KEY, String(v))
    // Minimising also drops out of maximize so the rail collapses cleanly.
    set(v ? { panelOpen: true } : { panelOpen: false, maximized: false })
  },

  togglePanel: () =>
    set((s) => {
      const next = !s.panelOpen
      localStorage.setItem(OPEN_KEY, String(next))
      return next ? { panelOpen: true } : { panelOpen: false, maximized: false }
    }),

  // Maximizing implies the panel is open.
  setMaximized: (v) => set(v ? { maximized: true, panelOpen: true } : { maximized: false }),

  setWidth: (v) => {
    const clamped = Math.max(MIN_DOCK_WIDTH, Math.min(MAX_DOCK_WIDTH, v))
    localStorage.setItem(WIDTH_KEY, String(clamped))
    set({ width: clamped })
  },

  setProjectModalOpen: (v) => set({ projectModalOpen: v }),

  setShowCompletedPlans: (v) => {
    localStorage.setItem(SHOW_DONE_KEY, String(v))
    set({ showCompletedPlans: v })
  },
}))
